import type { ReactNode } from "react";

/**
 * Minimal, dependency-free renderer for the Markdown subset Elm's
 * replies use: paragraphs and line breaks, bullet / numbered lists,
 * **bold**, *italic*, `code`, [label](url) links, and bare URLs.
 * Everything is built as React elements (never innerHTML), so reply
 * text can't inject markup.
 *
 * Links:
 * - Internal portal paths ("/contractors", "/help#shutdowns")
 *   navigate in-app via the onNavigate callback.
 * - External http(s) links open in a new window with
 *   rel="noopener noreferrer".
 * - mailto:/tel: render as plain anchors.
 * - Any other scheme (javascript:, data:, …) is refused and rendered
 *   as text.
 */

type Props = {
  text: string;
  onNavigate: (path: string) => void;
};

const INLINE_RE =
  /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(\[[^\]\n]+\]\([^()\s]+\))|(https?:\/\/[^\s<>()]+)/g;

const MD_LINK_RE = /^\[([^\]]+)\]\(([^()\s]+)\)$/;

function safeLinkKind(
  href: string,
): "internal" | "external" | "plain-anchor" | null {
  if (href.startsWith("/")) return "internal";
  if (/^https?:\/\//i.test(href)) return "external";
  if (/^(mailto|tel):/i.test(href)) return "plain-anchor";
  return null;
}

function trimTrailingPunctuation(url: string): [string, string] {
  // A bare URL at the end of a sentence usually drags punctuation
  // along ("see https://example.com."). Split it back off.
  const m = url.match(/[.,;:!?)]+$/);
  if (!m) return [url, ""];
  return [url.slice(0, url.length - m[0].length), m[0]];
}

function renderLink(
  href: string,
  label: string,
  key: string,
  onNavigate: (path: string) => void,
): ReactNode {
  const kind = safeLinkKind(href);
  const klass = "text-brand-700 underline hover:text-brand-800";
  if (kind === "internal") {
    return (
      <a
        key={key}
        href={href}
        className={klass}
        onClick={(e) => {
          e.preventDefault();
          onNavigate(href);
        }}
      >
        {label}
      </a>
    );
  }
  if (kind === "external") {
    return (
      <a
        key={key}
        href={href}
        className={klass}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  if (kind === "plain-anchor") {
    return (
      <a key={key} href={href} className={klass}>
        {label}
      </a>
    );
  }
  // Unsafe scheme — show the raw text, never a link.
  return label === href ? href : `${label} (${href})`;
}

function renderInline(
  text: string,
  keyBase: string,
  onNavigate: (path: string) => void,
): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const token = m[0];
    const key = `${keyBase}-${i++}`;
    if (token.startsWith("`")) {
      out.push(
        <code
          key={key}
          className="px-1 py-0.5 rounded bg-sand-100 text-[0.92em]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("**")) {
      out.push(
        <strong key={key} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("*")) {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith("[")) {
      const link = token.match(MD_LINK_RE);
      if (link) {
        out.push(renderLink(link[2]!, link[1]!, key, onNavigate));
      } else {
        out.push(token);
      }
    } else {
      // Bare URL
      const [url, trailing] = trimTrailingPunctuation(token);
      out.push(renderLink(url, url, key, onNavigate));
      if (trailing) out.push(trailing);
    }
    last = idx + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const BULLET_RE = /^\s*[-*•]\s+/;
const NUMBERED_RE = /^\s*\d+[.)]\s+/;

export function ChatMarkdown({ text, onNavigate }: Props) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    if (BULLET_RE.test(line) || NUMBERED_RE.test(line)) {
      const ordered = NUMBERED_RE.test(line);
      const re = ordered ? NUMBERED_RE : BULLET_RE;
      const items: ReactNode[] = [];
      while (i < lines.length && re.test(lines[i]!)) {
        items.push(
          <li key={`li-${key++}`}>
            {renderInline(lines[i]!.replace(re, ""), `k${key}`, onNavigate)}
          </li>,
        );
        i++;
      }
      blocks.push(
        ordered ? (
          <ol
            key={`ol-${key++}`}
            className="list-decimal pl-5 my-1 space-y-0.5"
          >
            {items}
          </ol>
        ) : (
          <ul key={`ul-${key++}`} className="list-disc pl-5 my-1 space-y-0.5">
            {items}
          </ul>
        ),
      );
      continue;
    }
    if (line.trim() === "") {
      // Blank line — paragraph gap. Collapse runs into one spacer.
      while (i < lines.length && lines[i]!.trim() === "") i++;
      blocks.push(<div key={`gap-${key++}`} className="h-2" />);
      continue;
    }
    blocks.push(
      <div key={`p-${key++}`}>
        {renderInline(line, `k${key}`, onNavigate)}
      </div>,
    );
    i++;
  }

  return <>{blocks}</>;
}
